import * as layout from '../../../components/markLayout.module.css';
import Table from '@hero/ui-kit/tables/Table';
import type { TableColumn } from '@hero/ui-kit/tables/Table/TableColHeader';
import { MarkButton } from '../../../components/MarkControls';
import React from 'react';
import { Link } from 'react-router-dom';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import InlineLoader from '../../../../../components/InlineLoader';
import QueryInlineError from '../../../../../components/QueryInlineError';
import useDripVersions from '../../../api/useDripVersions';
import useDripDiff from '../../../hooks/useDripDiff';
import DiffPanel from '../DiffPanel';
import type { DripDetail, DripVersionSummary } from '../../../types';
type Props = {
    drip: DripDetail;
};
const versionColumns = (dripId: number): TableColumn<DripVersionSummary>[] => [
    {
        colKey: 'Version',
        name: 'Version',
        render: (v) => {
            return (
                <>
                    <Link
                        to={`/mark/drips/${dripId}/versions/${v.version_number}`}
                        style={{
                            color: 'var(--mark-primary)',
                            textDecoration: 'none'
                        }}
                    >
                        v{v.version_number}
                    </Link>
                    {v.is_current && (
                        <Tag type="success" size="small">
                            current
                        </Tag>
                    )}
                    {v.is_draft && (
                        <Tag type="primary" size="small">
                            draft
                        </Tag>
                    )}
                </>
            );
        }
    },
    {
        colKey: 'Active instances',
        name: 'Active instances',
        align: 'right',
        headAlign: 'right',
        render: (v) => {
            return <>{v.active_instance_count.toLocaleString()}</>;
        }
    },
    {
        colKey: 'Send window',
        name: 'Send window',
        render: (v) => {
            return (
                <>
                    {v.send_window_start && v.send_window_end
                        ? `${v.send_window_start}–${v.send_window_end}`
                        : 'Default'}
                </>
            );
        }
    },
    {
        colKey: 'Created',
        name: 'Created',
        render: (v) => {
            return <>{new Date(v.created).toLocaleDateString()}</>;
        }
    }
];
const Versions: React.FC<Props> = ({ drip }) => {
    const [page, setPage] = React.useState(0);
    const limit = 20;
    const {
        data: versionsResponse,
        isLoading,
        error
    } = useDripVersions(drip.drip_id, {
        offset: page * limit,
        limit
    });
    const versions = versionsResponse?.data?.versions ?? [];
    const totalCount = versionsResponse?.data?.total_count ?? 0;
    const diff = useDripDiff(drip.current_version, drip.draft_version);
    return (
        <div>
            {diff && drip.current_version && drip.draft_version && (
                <DiffPanel
                    diff={diff}
                    draftVersion={drip.draft_version.version_number}
                    currentVersion={drip.current_version.version_number}
                />
            )}
            {isLoading ? (
                <InlineLoader />
            ) : error ? (
                <QueryInlineError error={error} />
            ) : versions.length === 0 ? (
                <P
                    styles={{
                        textAlign: 'center',
                        padding: '2rem 0'
                    }}
                >
                    No versions yet.
                </P>
            ) : (
                <div
                    style={{
                        overflowX: 'auto'
                    }}
                    className={layout.tableScroll}
                >
                    <Table
                        columns={versionColumns(drip.drip_id)}
                        data={versions}
                        rowKey="version_number"
                    />
                    {totalCount > limit && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '0.8rem',
                                marginTop: '1rem'
                            }}
                        >
                            <MarkButton
                                type="button"
                                disabled={page === 0}
                                onClick={() => setPage(page - 1)}
                            >
                                Previous
                            </MarkButton>
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    lineHeight: '2.4rem'
                                }}
                            >
                                Page {page + 1} of {Math.ceil(totalCount / limit)}
                            </P>
                            <MarkButton
                                variant="primary"
                                type="button"
                                disabled={(page + 1) * limit >= totalCount}
                                onClick={() => setPage(page + 1)}
                            >
                                Next
                            </MarkButton>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export default Versions;
